// Obrotnica anteny BEZPOSREDNIO z przegladarki (Web Serial) -- bez agenta.
// Osobny port szeregowy niz radio. Protokoly (czyste sterowniki, testowane):
//   gs232     -- Yaesu GS-232A/B i zgodne (G-5500, G-2800, ERC, K3NG / Arduino)
//   easycomm  -- EasyComm II (SatNOGS, K3NG, rozne kontrolery)
//   spid      -- SPID Rot1Prog / Rot2Prog (ramki binarne 13 bajtow)
import { useSyncExternalStore } from 'react';
import { tx } from './i18n';
import { isWebSerialSupported, findRememberedPort, portInfo } from './webSerialCat';

export const ROTOR_PRESETS = [
  { id: 'gs232', label: 'Yaesu GS-232A/B (G-5500, G-2800, K3NG, ERC)', baud: 9600 },
  { id: 'easycomm', label: 'EasyComm II', baud: 9600 },
  { id: 'spid', label: 'SPID Rot1Prog / Rot2Prog', baud: 600 },
];
export const ROTOR_BAUDS = [600, 1200, 4800, 9600, 19200, 115200, 460800];
const enc = (s) => new TextEncoder().encode(s);
const norm = (az) => ((Math.round(az) % 360) + 360) % 360;

export function gs232Driver() {
  let buf = '';
  return {
    poll: () => [enc('C\r')],
    feed(bytes) {
      buf += new TextDecoder().decode(bytes);
      const out = {};
      let i;
      while ((i = buf.search(/[\r\n]/)) >= 0) {
        const line = buf.slice(0, i).trim();
        buf = buf.slice(i + 1);
        const m = /^(?:AZ=|\+0)(\d{3})/.exec(line);      // GS-232B: "AZ=123", GS-232A: "+0123"
        if (m) out.az = parseInt(m[1], 10);
      }
      if (buf.length > 200) buf = '';
      return out;
    },
    goto: (az) => [enc(`M${String(norm(az)).padStart(3, '0')}\r`)],
    stop: () => [enc('S\r')],
  };
}

export function easycommDriver() {
  let buf = '';
  return {
    poll: () => [enc('AZ EL\n')],
    feed(bytes) {
      buf += new TextDecoder().decode(bytes);
      const out = {};
      let i;
      while ((i = buf.search(/[\r\n]/)) >= 0) {
        const line = buf.slice(0, i);
        buf = buf.slice(i + 1);
        const m = /AZ(-?\d+(?:\.\d+)?)/.exec(line);
        if (m) out.az = Math.round(parseFloat(m[1]));
      }
      if (buf.length > 200) buf = '';
      return out;
    },
    goto: (az) => [enc(`AZ${norm(az).toFixed(1)}\n`)],
    stop: () => [enc('SA SE\n')],
  };
}

export function spidDriver() {
  let buf = [];
  let ph = 2;                    // impulsy na stopien -- z odpowiedzi kontrolera (Rot1Prog: 1, Rot2Prog: 2)
  const digits = (n) => [...String(n).padStart(4, '0')].map(ch => ch.charCodeAt(0));
  // Status / stop: same zera; ustawienie: cyfry ASCII z (360 + kat) * impulsy, elewacja 0 stopni
  const zero = (k) => new Uint8Array([0x57, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, k, 0x20]);
  const cmd = (h, k) => new Uint8Array([0x57, ...h, ph, ...digits(ph * 360), ph, k, 0x20]);
  return {
    poll: () => [zero(0x1F)],
    feed(bytes) {
      buf.push(...bytes);
      const out = {};
      for (;;) {
        const s = buf.indexOf(0x57);
        if (s < 0) { buf = []; break; }
        if (buf.length - s < 12) { buf = buf.slice(s); break; }
        const f = buf.slice(s, s + 12);
        buf = buf.slice(s + 12);
        if (f[11] !== 0x20 || f.slice(1, 5).some(d => d > 9)) continue;
        if (f[5] >= 1 && f[5] <= 10) ph = f[5];
        out.az = norm(f[1] * 100 + f[2] * 10 + f[3] + f[4] / 10 - 360);
      }
      return out;
    },
    goto: (az) => [cmd(digits(ph * (360 + norm(az))), 0x2F)],
    stop: () => [zero(0x0F)],
  };
}

const makeDriver = (id) => (id === 'easycomm' ? easycommDriver() : id === 'spid' ? spidDriver() : gs232Driver());

// ── Polaczenie ────────────────────────────────────────────────────────────────
const STORE = 'webserial_rotor';
const POLL_MS = 1500;
let state = { status: 'disconnected', azimuth: null, error: null, presetId: null };
let lastSeen = 0;
const listeners = new Set();
const emit = (patch) => { state = { ...state, ...patch }; listeners.forEach(l => l()); };
let port = null, reader = null, driver = null, pollTimer = null, writeChain = Promise.resolve();

export function savedRotorConfig() {
  try { return JSON.parse(localStorage.getItem(STORE)) || null; } catch { return null; }
}

async function write(cmds) {
  writeChain = writeChain.then(async () => {
    if (!port?.writable) return;
    const w = port.writable.getWriter();
    try { for (const c of cmds) await w.write(c); } finally { w.releaseLock(); }
  }).catch(() => {});
  return writeChain;
}

async function readLoop() {
  while (port?.readable) {
    reader = port.readable.getReader();
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        const upd = driver.feed(value);
        if (upd.az != null) {
          lastSeen = Date.now();
          if (upd.az !== state.azimuth || state.status !== 'connected') emit({ status: 'connected', azimuth: upd.az, error: null });
        }
      }
    } catch (e) {
      if (port) emit({ error: String(e.message || e) });
      break;
    } finally {
      try { reader.releaseLock(); } catch { /* juz zwolniony */ }
    }
  }
}

export async function connectRotor({ presetId, baud, pick = true }) {
  if (!isWebSerialSupported()) throw new Error('Web Serial nie jest dostepny w tej przegladarce');
  const preset = ROTOR_PRESETS.find(p => p.id === presetId) || ROTOR_PRESETS[0];
  await disconnectRotor(false);
  emit({ status: 'connecting', error: null, presetId: preset.id });
  try {
    if (pick) port = await navigator.serial.requestPort();
    else {
      port = await findRememberedPort(savedRotorConfig()?.usb);
      if (!port) throw new Error('no-remembered-port');
    }
    await port.open({ baudRate: baud || preset.baud, dataBits: 8, parity: 'none', stopBits: 1, flowControl: 'none' });
    driver = makeDriver(preset.id);
    try { localStorage.setItem(STORE, JSON.stringify({ presetId: preset.id, baud: baud || preset.baud, usb: portInfo(port), auto: true })); } catch { /* brak storage */ }
    readLoop();
    const tick = () => write(driver.poll());
    tick();
    pollTimer = setInterval(() => {
      tick();
      if (lastSeen && Date.now() - lastSeen > 8000 && state.status !== 'connecting') {
        emit({ status: 'connecting', error: tx('Obrotnica nie odpowiada — sprawdź protokół i prędkość') });
      }
    }, POLL_MS);
  } catch (e) {
    port = null;
    const msg = e.name === 'NotFoundError' || e.message === 'no-remembered-port' ? null : String(e.message || e);
    emit({ status: 'disconnected', error: msg });
    if (msg) throw e;
  }
}

export async function disconnectRotor(forget = true) {
  clearInterval(pollTimer); pollTimer = null;
  const p = port; port = null;
  try { await reader?.cancel(); } catch { /* juz zamkniety */ }
  try { await writeChain; } catch { /* nic */ }
  try { await p?.close(); } catch { /* juz zamkniety */ }
  reader = null; driver = null; lastSeen = 0;
  if (forget) { try { const c = savedRotorConfig(); if (c) localStorage.setItem(STORE, JSON.stringify({ ...c, auto: false })); } catch { /* nic */ } }
  emit({ status: 'disconnected', azimuth: null });
}

export async function rotorGoto(az) {
  if (!driver || !port) throw new Error(tx('Obrotnica nie jest podłączona'));
  await write(driver.goto(az));
}
export async function rotorStop() {
  if (driver && port) await write(driver.stop());
}

export async function autoReconnectRotor() {
  const c = savedRotorConfig();
  if (!c?.auto || !isWebSerialSupported() || port) return;
  try { await connectRotor({ ...c, pick: false }); } catch { /* cicho */ }
}

if (isWebSerialSupported()) {
  navigator.serial.addEventListener?.('disconnect', (e) => {
    if (port && e.target === port) disconnectRotor(false);
  });
}

// Stan w formacie /api/rotor/status (albo null)
export function rotorAsStatus(r) {
  if (r.status !== 'connected' || r.azimuth == null) return null;
  return { connected: true, azimuth: r.azimuth, source: 'webserial' };
}

export function useWebSerialRotor() {
  return useSyncExternalStore((cb) => { listeners.add(cb); return () => listeners.delete(cb); }, () => state);
}
